import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type { RecommendationStrategy } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

interface SettingsRecommendationsCreateSeasonalStrategyButtonSignature {
  Args: {
    defaultStrategy?: RecommendationStrategy;
    isSomeStrategyCardBeingEdited?: boolean;
    seasonalStrategies: RecommendationStrategy[];
    setSomeStrategyCardBeingEditedState: (state: boolean) => void;
    settingsType: 'compset_positioning' | 'price_range';
  };
}

export default class SettingsRecommendationsCreateSeasonalStrategyButton extends Component<SettingsRecommendationsCreateSeasonalStrategyButtonSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onCreateSeasonalStrategyClicked() {
    if (this.args.defaultStrategy) {
      const seasonalStrategyPrefix = this.intl.t(
        'pricing_assistant.recommendations.seasonal_strategy',
        { count: '' },
      );
      // Logic to determine the newly created seasonal strategy name
      // Find the highest number in the existing seasonal strategy default names
      let maxNumber = 0;
      if (this.args.seasonalStrategies.length) {
        maxNumber = Math.max(
          ...this.args.seasonalStrategies.map((strategy) => {
            const pattern = new RegExp(`^${seasonalStrategyPrefix}(\\d+)`);
            const match = strategy.name.match(pattern);
            return match && match[1] ? parseInt(match[1], 10) : 0;
          }),
        );
      }

      this.store.createRecord<RecommendationStrategy>(
        'recommendation-strategy',
        {
          seasonStartDt: null,
          seasonEndDt: null,
          conditions: null,
          conditionalStrategies: [],
          settings: this.args.defaultStrategy.settings,
          name: this.intl.t(
            'pricing_assistant.recommendations.seasonal_strategy',
            {
              count: maxNumber + 1,
            },
          ),
          // @ts-expect-error: Type 'string | null' is not assignable to type 'number | undefined'.
          subscriptionId: this.hotelAndMarketService.hotel.id,
          settingsType: this.args.settingsType,
        },
      );

      this.args.setSomeStrategyCardBeingEditedState(true);
    }
  }

  <template>
    <PrismButton
      @buttonType="primary"
      @isDisabled={{@isSomeStrategyCardBeingEdited}}
      @label={{t "pricing_assistant.recommendations.create_seasonal_strategy"}}
      @onClick={{this.onCreateSeasonalStrategyClicked}}
      data-test-button
    />
  </template>
}
